import type { PDFImage } from "pdf-lib";
import { PDFDocument } from "pdf-lib";
import pdfMake from "pdfmake/build/pdfmake";

import type { Consultation } from "@/types/api/consultation";
import type { Patient } from "@/types/api/patient";
import { getName } from "@/utils/patient";
import { exportPatientPDF } from "@/utils/patientExport";

export const handleGenerateAndMerge = async (
  patient: Patient,
  consultations: Array<Consultation>,
  selectedDocuments: Array<{
    id: string;
    title: string;
    isPdf: boolean;
    date: Date;
    src: string;
  }>
) => {
  const fullName = getName(patient);

  // Step 1: Generate the core patient PDF
  const pdfBlob = await new Promise<Blob>((resolve) => {
    const docGenerator = pdfMake.createPdf(
      exportPatientPDF(patient, consultations)
    );
    docGenerator.getBlob().then((blob) => resolve(blob));
  });

  const mergedPdf = await PDFDocument.create();

  // Step 2: Append the main PDF
  const mainPdf = await PDFDocument.load(await pdfBlob.arrayBuffer());
  const copiedPages = await mergedPdf.copyPages(
    mainPdf,
    mainPdf.getPageIndices()
  );
  copiedPages.forEach((page) => mergedPdf.addPage(page));

  // Config
  const A4_WIDTH = 595.28;
  const A4_HEIGHT = 841.89;
  const MARGIN = 20;
  const ROW_HEIGHT = 200;

  let page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
  let cursorX = MARGIN;
  let cursorY = A4_HEIGHT - MARGIN - ROW_HEIGHT;

  // Step 3: Append selected documents
  for (const doc of selectedDocuments) {
    const res = await fetch(doc.src);
    const buffer = await res.arrayBuffer();
    const contentType = res.headers.get("Content-Type") || "";

    if (doc.isPdf) {
      const docPdf = await PDFDocument.load(buffer);
      const docPages = await mergedPdf.copyPages(
        docPdf,
        docPdf.getPageIndices()
      );
      docPages.forEach((docPage) => mergedPdf.addPage(docPage));
    } else {
      let image: PDFImage;
      if (contentType.includes("jpeg") || contentType.includes("jpg")) {
        image = await mergedPdf.embedJpg(buffer);
      } else if (contentType.includes("png")) {
        image = await mergedPdf.embedPng(buffer);
      } else {
        console.warn(`Unsupported image format: ${contentType}`);
        continue;
      }

      const { width, height } = image;
      const scale = ROW_HEIGHT / height;
      const scaledWidth = width * scale;
      const scaledHeight = height * scale;

      // Does it fit horizontally?
      if (cursorX + scaledWidth > A4_WIDTH - MARGIN) {
        // Move to new row
        cursorX = MARGIN;
        cursorY -= ROW_HEIGHT + MARGIN;
      }

      // Does it fit vertically?
      if (cursorY < MARGIN) {
        // New page
        page = mergedPdf.addPage([A4_WIDTH, A4_HEIGHT]);
        cursorX = MARGIN;
        cursorY = A4_HEIGHT - MARGIN - ROW_HEIGHT;
      }

      // Draw the image
      page.drawImage(image, {
        x: cursorX,
        y: cursorY,
        width: scaledWidth,
        height: scaledHeight,
      });

      // Move cursor horizontally
      cursorX += scaledWidth + MARGIN;
    }
  }

  // Step 4: Finalize & download
  const finalPdfBytes = await mergedPdf.save();
  const blob = new Blob([new Uint8Array(finalPdfBytes)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fiche-${fullName.replace(" ", "-")}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};
